/**
 * Passe PHOTOS du regroupement — rattache chaque marqueur photo à un groupe
 * (ou le laisse autonome) sur la géométrie RÉELLE, par règles en cascade :
 *
 * 1. POINTAGE : la flèche vise un groupe — cône depuis sa base (cf.
 *    ARROW_CONE_HALF_ANGLE_DEG / ARROW_REACH_RATIO), le premier touché le
 *    long de la visée gagne. Un groupe est visé par son enveloppe convexe
 *    autant que par ses tracés : trois fissures parallèles forment une zone,
 *    la flèche qui passe entre elles les désigne ;
 * 2. ESPACE NET : l'ancre est DANS l'enveloppe d'un groupe, ou le 2e candidat
 *    est au moins PHOTO_CLARITY_RATIO fois plus loin que le 1er ;
 * 3. TEMPS ÉVIDENT : plusieurs candidats à distance comparable, mais un seul
 *    posé dans la fenêtre du geste (TEMPORAL_GAP_MS) ;
 * 4. AMBIGU : le meilleur est retenu mais signalé — l'IA tranche parmi les
 *    candidats listés, l'inspecteur voit un « ? » ;
 * 5. AUTONOME : aucun groupe à portée → vue d'ensemble.
 *
 * La forme du marqueur (rond / flèche) n'est PAS une règle : la convention
 * change d'un inspecteur à l'autre. Seule la direction d'une flèche compte.
 * Le temps n'intervient qu'en 3 : une photo ajoutée le lendemain sur un
 * désordre collé est rattachée pareil.
 *
 * Module pur : il ne connaît ni le moteur, ni `grouping.ts` (qui l'appelle).
 */

import {
  bboxCenter,
  bboxCorners,
  bboxDiagonal,
  bboxUnion,
  coneHitDistance,
  convexHull,
  pointInPolygon,
  pointPolylineDistance,
  unitVector,
} from "./geometry";
import {
  ARROW_CONE_HALF_ANGLE_DEG,
  ARROW_REACH_RATIO,
  ATTACH_FLOOR_RATIO,
  ATTACH_GAP_RATIO,
  ATTACH_SIZE_FACTOR,
  PHOTO_CLARITY_RATIO,
  PHOTO_SATURATION_PENALTY,
  PHOTO_TIE_FLOOR_RATIO,
  TEMPORAL_GAP_MS,
} from "./grouping-constants";
import { MANY_MARKERS_COUNT } from "./confidence";
import type {
  AnalysisEntity,
  PhotoAttachmentRule,
  Point,
  WorldBbox,
} from "./types";

/** Une polyligne (ouverte ou fermée) de la géométrie d'une cible. */
export interface PhotoShape {
  points: Point[];
  closed: boolean;
}

/** Cible de rattachement : un groupe de tracés, ou une note hors plan. */
export interface PhotoTarget {
  shapes: PhotoShape[];
  /** Diagonale de l'emprise de la cible — rayon de rattachement adaptatif. */
  diagonal: number;
  /** `createdAt` des membres horodatés (départage temporel). */
  times: number[];
  /** Photos déjà rattachées (pénalité de saturation). */
  markerCount: number;
}

export interface PhotoCandidate {
  target: number;
  /** Distance à la géométrie réelle, en part de l'échelle (0 = dedans). */
  distance: number;
  /** La flèche du marqueur vise cette cible. */
  pointed: boolean;
  /** L'ancre du marqueur est dans l'enveloppe convexe de la cible. */
  inside: boolean;
  /** Écart temporel à la plage de la cible (ms), null si non horodaté. */
  deltaT: number | null;
  /** Photos déjà rattachées à la cible au moment de la décision. */
  markerCount: number;
}

export interface PhotoDecision {
  marker: AnalysisEntity;
  /** Index de la cible retenue, null pour une photo autonome. */
  target: number | null;
  rule: PhotoAttachmentRule;
  /**
   * Distance à la cible retenue (part de l'échelle) ; pour une photo
   * autonome, celle de la cible la plus proche (null s'il n'y en a aucune).
   */
  distance: number | null;
  /** Candidats à portée : le retenu en tête, puis du plus proche au plus loin. */
  candidates: PhotoCandidate[];
}

const isMarker = (e: AnalysisEntity): boolean => e.markerLabel !== null;

/** Rayon de rattachement adaptatif d'une cible (cf. grouping-constants.ts). */
export function attachRadius(diagonal: number, scale: number): number {
  return Math.min(
    ATTACH_GAP_RATIO * scale,
    Math.max(ATTACH_FLOOR_RATIO * scale, ATTACH_SIZE_FACTOR * diagonal),
  );
}

/**
 * Géométrie d'une entité pour la passe photos : ses points réels quand le
 * scan les a lus, sinon sa bbox (contour fermé) ; plus un segment par leader
 * (pointe → texte), car la photo est souvent posée le long du leader.
 */
export function entityShapes(entity: AnalysisEntity): PhotoShape[] {
  const bbox = entity.bbox;
  if (!bbox) return [];
  const shapes: PhotoShape[] = [];
  if (entity.points && entity.points.length > 0) {
    shapes.push({ points: entity.points, closed: entity.closed ?? false });
  } else {
    shapes.push({ points: bboxCorners(bbox), closed: true });
  }
  if (entity.leaderTips?.length) {
    const center = bboxCenter(bbox);
    for (const tip of entity.leaderTips) {
      shapes.push({ points: [tip, center], closed: false });
    }
  }
  return shapes;
}

/** Cible construite depuis les membres d'un groupe (tracés + textes rattachés). */
export function buildPhotoTarget(members: AnalysisEntity[]): PhotoTarget {
  const withBbox = members.filter((m) => m.bbox !== null);
  let bbox: WorldBbox | null = null;
  for (const m of withBbox) {
    bbox = bbox ? bboxUnion(bbox, m.bbox as WorldBbox) : (m.bbox as WorldBbox);
  }
  return {
    shapes: withBbox.flatMap(entityShapes),
    diagonal: bbox ? bboxDiagonal(bbox) : 0,
    times: members
      .map((m) => m.createdAt)
      .filter((t): t is number => t !== null),
    markerCount: members.filter(isMarker).length,
  };
}

/** Écart (ms) d'un horodatage à une plage, 0 dedans, null si inconnu. */
function temporalGap(createdAt: number | null, times: number[]): number | null {
  if (createdAt === null || times.length === 0) return null;
  const min = Math.min(...times);
  const max = Math.max(...times);
  if (createdAt >= min && createdAt <= max) return 0;
  return createdAt < min ? min - createdAt : createdAt - max;
}

/** Ordre de traitement : chronologie (une photo posée tôt a priorité), puis handle. */
function compareMarkers(a: AnalysisEntity, b: AnalysisEntity): number {
  const ta = a.createdAt ?? Number.POSITIVE_INFINITY;
  const tb = b.createdAt ?? Number.POSITIVE_INFINITY;
  if (ta !== tb) return ta - tb;
  return a.handle.localeCompare(b.handle);
}

interface ScoredCandidate extends PhotoCandidate {
  /** Distance pénalisée (saturation) — la clé de classement. */
  rank: number;
  /** Distance de contact le long de la visée (part de l'échelle), si visée. */
  hit: number | null;
  /** Diagonale de la cible : à égalité, la plus petite (la plus précise) prime. */
  diagonal: number;
}

/**
 * Rattache les marqueurs aux cibles. `scale` est la référence des seuils
 * relatifs (diagonale de la vignette, ou du plan). Les décisions sont prises
 * dans l'ordre chronologique des photos ; chaque rattachement compte pour la
 * saturation des suivantes.
 */
export function assignPhotos(
  markers: AnalysisEntity[],
  targets: PhotoTarget[],
  scale: number,
): PhotoDecision[] {
  const counts = targets.map((t) => t.markerCount);
  const hulls = targets.map((t) => convexHull(t.shapes.flatMap((s) => s.points)));
  const halfAngle = (ARROW_CONE_HALF_ANGLE_DEG * Math.PI) / 180;
  const reach = ARROW_REACH_RATIO * scale;
  const isTie = (best: number, other: number): boolean =>
    other - best <= PHOTO_TIE_FLOOR_RATIO || other <= best * PHOTO_CLARITY_RATIO;
  const decisions: PhotoDecision[] = [];

  const ordered = markers.filter((m) => m.bbox !== null).sort(compareMarkers);
  for (const marker of ordered) {
    const center = bboxCenter(marker.bbox as WorldBbox);
    const anchor = marker.markerTip ?? center;
    const origin = marker.markerBase ?? center;
    const direction = marker.markerTip
      ? unitVector(origin, marker.markerTip)
      : null;

    const scored: ScoredCandidate[] = [];
    let nearest: number | null = null;
    targets.forEach((target, index) => {
      const hull = hulls[index];
      const inside = pointInPolygon(anchor, hull);
      const raw = inside
        ? 0
        : Math.min(
            ...target.shapes.map((s) =>
              pointPolylineDistance(anchor, s.points, s.closed),
            ),
            Number.POSITIVE_INFINITY,
          );
      if (!Number.isFinite(raw)) return;
      const distance = raw / scale;
      if (nearest === null || distance < nearest) nearest = distance;

      let hit: number | null = null;
      if (direction) {
        if (pointInPolygon(origin, hull)) hit = 0;
        else {
          const aimed = [...target.shapes, { points: hull, closed: hull.length >= 3 }];
          for (const shape of aimed) {
            const h = coneHitDistance(
              origin,
              direction,
              halfAngle,
              reach,
              shape.points,
              shape.closed,
            );
            if (h !== null && (hit === null || h < hit)) hit = h;
          }
        }
      }

      const eligible =
        inside || hit !== null || raw <= attachRadius(target.diagonal, scale);
      if (!eligible) return;
      const penalty =
        counts[index] >= MANY_MARKERS_COUNT ? PHOTO_SATURATION_PENALTY : 1;
      scored.push({
        target: index,
        distance,
        pointed: hit !== null,
        inside,
        deltaT: temporalGap(marker.createdAt, target.times),
        markerCount: counts[index],
        rank: distance * penalty,
        hit: hit === null ? null : hit / scale,
        diagonal: target.diagonal,
      });
    });

    if (scored.length === 0) {
      decisions.push({
        marker,
        target: null,
        rule: "standalone",
        distance: nearest,
        candidates: [],
      });
      continue;
    }

    // ── Cascade : pointage → enveloppe → plus proche → temps → ambigu ──────
    let rule: PhotoAttachmentRule;
    let tied: ScoredCandidate[];
    const pointed = scored
      .filter((c) => c.pointed)
      .sort((a, b) => (a.hit as number) - (b.hit as number) || a.rank - b.rank);
    const inside = scored.filter((c) => c.inside);
    if (pointed.length > 0) {
      const first = pointed[0].hit as number;
      tied = pointed.filter((c) => isTie(first, c.hit as number));
      rule = "pointed";
    } else if (inside.length > 0) {
      tied = inside;
      rule = "inside";
    } else {
      const byRank = [...scored].sort((a, b) => a.rank - b.rank);
      tied = byRank.filter((c) => isTie(byRank[0].rank, c.rank));
      rule = "nearest";
    }

    let chosen: ScoredCandidate;
    if (tied.length === 1) {
      chosen = tied[0];
    } else {
      const inWindow = tied.filter(
        (c) => c.deltaT !== null && c.deltaT <= TEMPORAL_GAP_MS,
      );
      if (inWindow.length === 1) {
        chosen = inWindow[0];
        rule = "temporal";
      } else {
        // Ambigu : le mieux classé, puis la cible la plus précise (petite),
        // puis la moins photographiée.
        chosen = [...tied].sort(
          (a, b) =>
            a.rank - b.rank ||
            a.diagonal - b.diagonal ||
            a.markerCount - b.markerCount ||
            a.target - b.target,
        )[0];
        rule = "ambiguous";
      }
    }
    // Saturation sans pointage ni enveloppe : rattaché, mais déclaré incertain.
    if (
      (rule === "nearest" || rule === "temporal") &&
      chosen.markerCount >= MANY_MARKERS_COUNT
    ) {
      rule = "ambiguous";
    }
    counts[chosen.target]++;

    const others = scored
      .filter((c) => c !== chosen)
      .sort((a, b) => a.rank - b.rank);
    const strip = (c: ScoredCandidate): PhotoCandidate => ({
      target: c.target,
      distance: c.distance,
      pointed: c.pointed,
      inside: c.inside,
      deltaT: c.deltaT,
      markerCount: c.markerCount,
    });
    decisions.push({
      marker,
      target: chosen.target,
      rule,
      distance: chosen.distance,
      candidates: [chosen, ...others].map(strip),
    });
  }
  return decisions;
}
